import React, { useState, useEffect, useRef } from 'react';
import { Bot, Send, ChevronDown, ExternalLink, TrendingUp, Shield, Settings, Users, Zap, AlertTriangle, Terminal, Menu, X, LineChart, MessageSquare, BarChart3, PieChart } from 'lucide-react';
import { FeatureWidgets } from './FeatureWidgets';
import BlockchainStatus from './BlockchainStatus';
import EasterEgg from './EasterEgg';
import ParticleBackground from './ParticleBackground';
import { ProcessingAnimation } from './ProcessingAnimation';
import TokenomicsSection from './TokenomicsSection';



const RevenueStory = () => {
  return (
    <div className="space-y-16 animate-fadeIn">
      {/* Chapter 1: Trading Excellence */}
      <div className="space-y-4">
        <h3 className="text-2xl font-bold text-green-400">Trading Excellence</h3>
        <p className="text-green-300/70 leading-relaxed">
          Our advanced trading engine is projected to capture 2% of the daily DEX trading volume, 
          generating an estimated $5.2M in trading fees by month 24. This is driven by our competitive 
          fee structure of 0.1% for standard trades and 0.2% for auto-trades.
        </p>
      </div>

      {/* Chapter 2: Community Building */}
      <div className="space-y-4">
        <h3 className="text-2xl font-bold text-green-400">Community Building</h3>
        <p className="text-green-300/70 leading-relaxed">
          Our tiered membership system offers subscription options from $xx to $xxx monthly, 
          with token holding alternatives of XXX to XXXX $OPTR. Subscription revenue is expected to 
          generate $1.5M monthly by year 2, driven by our expanding user base and premium features 
          like the professional trading terminal.
        </p>
      </div>

      {/* Chapter 3: Token Economics */}
      <div className="space-y-4">
        <h3 className="text-2xl font-bold text-green-400">Token Economics</h3>
        <p className="text-green-300/70 leading-relaxed">
          The $OPTR token's 5% trading tax and growing utility within our ecosystem is projected 
          to contribute $1.0M monthly by the end of year 2, creating a sustainable revenue stream 
          that grows with our platform's adoption.
        </p>
      </div>

      {/* Chapter 4: The Road Ahead */}
      <div className="space-y-4">
        <h3 className="text-2xl font-bold text-green-400">The Road Ahead</h3>
        <p className="text-green-300/70 leading-relaxed">
          Combined monthly revenue is projected to reach $7.7M+ by month 24, driven by our 
          multi-stream approach and growing market presence. This positions us for sustainable 
          long-term growth and continued platform development.
        </p>
      </div>
    </div>
  );
};

const LandingPage = () => {
  const [currentHeadline, setCurrentHeadline] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [titleOpacity, setTitleOpacity] = useState(1);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const cursorRef = useRef<HTMLDivElement>(null);
  const [isStoryView, setIsStoryView] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const tokenAddress = "0x72b658Bd674f9c2B4954682f517c17D14476e417"; // Replace with actual address
  const [showStickyCTA, setShowStickyCTA] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [gameMode, setGameMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Check if device is mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check on initial load
    checkIfMobile();
    
    // Listen for resize events
    window.addEventListener('resize', checkIfMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const copyToClipboard = async () => {
    console.log('Copy button clicked'); // Debug log
    try {
      await window.navigator.clipboard.writeText(tokenAddress);
      console.log('Copy successful'); // Debug log
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Copy failed:', err); // Debug log
      // Fallback method
      const textArea = document.createElement('textarea');
      textArea.value = tokenAddress;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        console.log('Fallback copy successful'); // Debug log
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Fallback copy failed:', err); // Debug log
      }
      document.body.removeChild(textArea);
    }
  };

  const headlines = [
    {
      title: "Deploy your trading agent",
      subtitle: "Set your goal. It finds entries and exits for you."
    },
    {
      title: "Autotrade while you sleep",
      subtitle: "Your strategy runs 24/7 with protected routing."
    },
    {
      title: "See moves early",
      subtitle: "Instant alerts when momentum, wallets, or liquidity shift."
    },
    {
      title: "Deploy complex strategies instantly",
      subtitle: "Launch sequences of orders on any token with one command."
    },
    {
      title: "Ask the chain in plain English",
      subtitle: "Ask questions. Get on-chain answers you can act on."
    },
    {
      title: "Start in minutes",
      subtitle: "Get 350 OPTR, verify in Telegram, deploy your agent."
    }
  ];

  const membershipTiers = [
    {
      name: "Free",
      comingSoon: true,
      tokenRequirement: "0",
      color: "green-400"
    },
    {
      name: "Rookie",
      comingSoon: true,
      tokenRequirement: "350",
      color: "green-400"
    },
    {
      name: "Trader",
      comingSoon: true,
      tokenRequirement: "1000",
      color: "green-400"
    },
    {
      name: "Pro",
      comingSoon: true,
      highlight: true,
      tokenRequirement: "2000",
      color: "green-400"
    },
    {
      name: "Elite",
      comingSoon: true,
      highlight: true,
      tokenRequirement: "3500",
      color: "green-400"
    }
  ];

  interface MembershipFeature {
    name: string;
    tiers?: string[];
    levels?: Record<string, string>;
  }

  const membershipFeatures: MembershipFeature[] = [
    { name: "Blockchain Chat", tiers: ["Free", "Rookie", "Trader", "Pro", "Elite"] },
    { name: "Token Scanning", tiers: ["Free", "Rookie", "Trader", "Pro", "Elite"] },
    { name: "Manual Trading", tiers: ["Free", "Rookie", "Trader", "Pro", "Elite"] },
    { name: "Autotrade Simulation", tiers: ["Free", "Rookie", "Trader", "Pro", "Elite"] },
    { name: "Access to Trading Academy", tiers: ["Rookie", "Trader", "Pro", "Elite"] },
    { name: "AI Trending Channel", tiers: ["Rookie", "Trader", "Pro", "Elite"] },
    { name: "Top Wallet Signals", tiers: ["Rookie", "Trader", "Pro", "Elite"] },
    { name: "Autotrade Simulation with Notifications", tiers: ["Rookie", "Trader", "Pro", "Elite"] },
    { name: "Basic Trading Strategies", tiers: ["Rookie", "Trader","Pro", "Elite"] },
    { name: "Low-Risk Signals", tiers: ["Trader", "Pro", "Elite"] },
    { name: "Medium-Risk Signals", tiers: ["Pro", "Elite"] },
    { name: "High-Risk Signals", tiers: ["Elite"] },
    { name: "Degen Signals", tiers: ["Elite"] },
    { name: "Dump & Pump Signals", tiers: ["Elite"] },
    { name: "Golden Signals", tiers: ["Trader", "Pro", "Elite"] },
    { name: "AutoSnipe Signals", tiers: ["Pro", "Elite"] },
    { name: "AutoSnipe with priority", tiers: ["Elite"] },
    { name: "Terminal Access", tiers: ["Pro", "Elite"] },
    { name: "1-Click Autotrade Strategies", tiers: ["Rookie", "Trader", "Pro", "Elite"] },
    { name: "Autotrade on Mainnet", tiers: ["Rookie", "Trader","Pro", "Elite"] },
    { name: "Smart Trading Bot", levels: { "Free": "Basic", "Rookie": "Faster", "Trader": "Even Faster", "Pro": "Ultra-Fast", "Elite": "Lightning-Fast" } },
    { name: "Parallel autotrading agents", levels: { "Rookie": "1", "Trader": "2", "Pro": "5", "Elite": "10" } }
  ];

  const featuresList = [
    {
      title: "Proprietary Trading Engine",
      icon: <Zap className="w-6 h-6" />,
      description: "Trade faster than anyone else with our superior execution speed. Get better prices and avoid slippage while competitors are still loading.",
      color: "emerald",
      gradient: "from-emerald-500 to-green-500"
    },
    {
      title: "Real-Time Market Scanner",
      icon: <TrendingUp className="w-6 h-6" />,
      description: "Never miss profitable opportunities again. Get instant alerts when big money moves, new tokens launch, or market conditions change.",
      color: "blue",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      title: "AI-Powered Analysis",
      icon: <Bot className="w-6 h-6" />,
      description: "Let AI do the heavy lifting. Our system spots patterns you'd miss and predicts moves before they happen, giving you the edge.",
      color: "purple",
      gradient: "from-purple-500 to-violet-500"
    },
    {
      title: "Auto-Trade Strategy",
      icon: <Terminal className="w-6 h-6" />,
      description: "Make money while you sleep. Set your strategy once and let our bots execute trades 24/7, never missing opportunities.",
      color: "orange",
      gradient: "from-orange-500 to-amber-500"
    },
    {
      title: "Advanced Analysis Tools",
      icon: <LineChart className="w-6 h-6" />,
      description: "See what others can't. Professional-grade insights help you make smarter decisions and avoid costly mistakes.",
      color: "teal",
      gradient: "from-teal-500 to-emerald-500"
    },
    {
      title: "AI Chat Assistant",
      icon: <MessageSquare className="w-6 h-6" />,
      description: "Get instant answers to any trading question. Ask about tokens, strategies, or market conditions in plain English.",
      color: "pink",
      gradient: "from-pink-500 to-rose-500"
    },
    {
      title: "Risk Management",
      icon: <Shield className="w-6 h-6" />,
      description: "Protect your capital with smart risk assessment. Avoid rug pulls and bad contracts before they cost you money.",
      color: "red",
      gradient: "from-red-500 to-pink-500"
    },
    {
      title: "Custom Trading Settings",
      icon: <Settings className="w-6 h-6" />,
      description: "Trade your way. Customize everything to match your style and risk tolerance for maximum comfort and profit.",
      color: "indigo",
      gradient: "from-indigo-500 to-purple-500"
    },
    {
      title: "Community Insights",
      icon: <Users className="w-6 h-6" />,
      description: "Learn from the best. Access exclusive signals and strategies from successful traders who've already made millions.",
      color: "yellow",
      gradient: "from-yellow-500 to-orange-500"
    }
  ];

  interface FeatureDetailsMap {
    [key: string]: {
      benefits: string[];
      metrics: Record<string, string>;
    };
  }

  const featureDetails: FeatureDetailsMap = {
    "Real-Time Market Scanner": {
      benefits: [
        "Catch opportunities before they're gone",
        "Get alerts when whales move",
        "Spot new tokens instantly",
        "Never miss market shifts",
        "Avoid FOMO with early warnings"
      ],
      metrics: {
        "Opportunities caught": "Daily",
        "Early alerts": "Minutes ahead",
        "Market coverage": "Complete",
        "False signals": "Minimal",
        "Profit potential": "High"
      }
    },
    "Proprietary Trading Engine": {
      benefits: [
        "Beat other traders to the punch",
        "Get better prices every time",
        "Avoid costly slippage",
        "Trade when others can't",
        "Maximize your profits"
      ],
      metrics: {
        "Speed advantage": "Massive",
        "Price improvement": "Consistent",
        "Slippage reduction": "Significant",
        "Success rate": "Near perfect",
        "Profit boost": "Measurable"
      }
    },
    "AI-Powered Analysis": {
      benefits: [
        "Spot patterns humans miss",
        "Predict moves before they happen",
        "Learn from every trade",
        "Improve over time",
        "Give you the edge"
      ],
      metrics: {
        "Pattern recognition": "Superior",
        "Prediction accuracy": "High",
        "Learning capability": "Continuous",
        "Edge over manual": "Clear",
        "ROI improvement": "Substantial"
      }
    },
    "Auto-Trade Strategy": {
      benefits: [
        "Make money while you sleep",
        "Never miss opportunities",
        "Execute perfectly every time",
        "Work 24/7 without breaks",
        "Scale your trading"
      ],
      metrics: {
        "Uptime": "24/7",
        "Opportunities caught": "All",
        "Execution quality": "Perfect",
        "Scalability": "Unlimited",
        "Profit consistency": "Reliable"
      }
    },
    "Advanced Analysis Tools": {
      benefits: [
        "See what others can't",
        "Make smarter decisions",
        "Avoid costly mistakes",
        "Understand market depth",
        "Trade with confidence"
      ],
      metrics: {
        "Insight depth": "Professional",
        "Decision quality": "Improved",
        "Mistake reduction": "Significant",
        "Confidence level": "High",
        "Success rate": "Better"
      }
    },
    "AI Chat Assistant": {
      benefits: [
        "Get instant expert advice",
        "Ask questions in plain English",
        "Understand complex concepts",
        "Make informed decisions",
        "Learn while you trade"
      ],
      metrics: {
        "Response speed": "Instant",
        "Answer quality": "Expert level",
        "Learning curve": "Minimal",
        "Decision support": "Comprehensive",
        "User satisfaction": "High"
      }
    },
    "Risk Management": {
      benefits: [
        "Protect your capital",
        "Avoid rug pulls",
        "Spot red flags early",
        "Trade with confidence",
        "Sleep better at night"
      ],
      metrics: {
        "Capital protection": "Strong",
        "Rug pull detection": "Early",
        "Risk reduction": "Significant",
        "Peace of mind": "Complete",
        "Loss prevention": "Proven"
      }
    },
    "Custom Trading Settings": {
      benefits: [
        "Trade exactly how you want",
        "Match your risk tolerance",
        "Optimize for your style",
        "Feel comfortable trading",
        "Maximize your potential"
      ],
      metrics: {
        "Customization": "Complete",
        "Comfort level": "High",
        "Style matching": "Perfect",
        "Risk alignment": "Precise",
        "Performance": "Optimized"
      }
    },
    "Community Insights": {
      benefits: [
        "Learn from millionaire traders",
        "Get exclusive signals",
        "Access proven strategies",
        "Network with winners",
        "Accelerate your success"
      ],
      metrics: {
        "Expert access": "Direct",
        "Signal quality": "Premium",
        "Strategy success": "Proven",
        "Network value": "Priceless",
        "Success acceleration": "Fast"
      }
    }
  };

  const roadmap = [
    {
      phase: "Phase 1 - Product Development",
      status: "completed",
      timestamp: "2022-2023",
      completion: "100%",
      items: [        
        { text: "Initial Technology Stack Development", status: "completed", date: "2022" },
        { text: "AI Analysis Engine Creation", status: "completed", date: "2023" },
        { text: "Advanced Signal Generation System", status: "completed", date: "2023" }
      ]
    },
    {
      phase: "Phase 2 - Platform Perfection",
      status: "completed",
      timestamp: "2024",
      completion: "100%",
      items: [
        { text: "Enhanced Trading Features", status: "completed", date: "Q1 2024" },
        { text: "AI Model Optimization", status: "completed", date: "Q2 2024" },
        { text: "Platform Stability & Performance", status: "completed", date: "H2 2024" }
      ]
    },
    {
      phase: "Phase 3 - Token Launch",
      status: "completed",
      timestamp: "2025 Q1",
      completion: "100%",
      items: [
        { text: "$OPTR Token Launch", status: "completed", date: "Feb 2025" },
        { text: "Membership Tier Activation", status: "completed", date: "Feb 2025" },
        { text: "Global User Base Growth & Engagement", status: "completed", date: "Q1 2025" }
      ]
    },
    {
      phase: "Phase 4 - Terminal Release",
      status: "completed",
      timestamp: "2025 Q2",
      completion: "100%",
      items: [
        { text: "AI Trading Agents Development", status: "completed", date: "Q2 2025" },        
        { text: "Advanced Trading Features", status: "completed", date: "Q2 2025" },
        { text: "Professional Trading Terminal", status: "completed", date: "Q2 2025" }
       
      ]
    },
    {
      phase: "Phase 5 - Growth & Optimization",
      status: "current",
      timestamp: "2025 Q3-Q4", 
      completion: "60%",
      items: [
        { text: "Sequences Implementation", status: "completed", date: "Q3 2025" },
        { text: "Performance Optimization", status: "completed", date: "Q3 2025" },
        { text: "Smart-Strategy Implementation", status: "completed", date: "Q3 2025" },
        { text: "Agent Marketplace", status: "in_progress", date: "Q4 2025" }
      ]
    },
    {
      phase: "Phase 6 - Global Expansion",
      status: "next",
      timestamp: "2026 Q1-Q2", 
      completion: "0%",
      items: [
        { text: "Multichain Integration", status: "planned", date: "Q1 2026" },
        { text: "International Market Expansion", status: "planned", date: "Q2 2026" },
        { text: "Advanced Cross-Chain Features", status: "planned", date: "Q2 2026" }
      ]
    }
  ];

  const [expandedFeature, setExpandedFeature] = useState<string | null>(null);

  const faqItems = [
    {
      question: "What is opTrade AI?",
      answer: "opTrade AI is an advanced trading platform that combines artificial intelligence with blockchain technology to provide automated trading strategies, real-time market analysis, and AI-powered insights for cryptocurrency traders."
    },
    {
      question: "How does the $OPTR token work?",
      answer: "The $OPTR token is the utility token powering the opTrade AI ecosystem. It grants access to membership tiers, provides trading fee discounts, enables governance participation, and accumulates value through the platform's revenue sharing model."
    },
    {
      question: "What are the membership tiers?",
      answer: "opTrade AI offers four membership tiers: Rookie, Trader, Pro, and Elite. Each tier provides escalating benefits and features, with access determined by the amount of $OPTR tokens held or a monthly subscription fee."
    },
    {
      question: "Is my data and trading information secure?",
      answer: "Yes, security is our priority. We implement enterprise-grade encryption, secure authentication protocols, and regular security audits. Your trading data is protected and never shared with third parties without your explicit consent."
    },
    {
      question: "How does AI improve my trading?",
      answer: "Our AI analyzes vast amounts of on-chain data, market patterns, and historical performance to identify trading opportunities, optimize entry and exit points, provide risk assessments, and execute automated strategies based on your preferences."
    },
    {
      question: "Can I use opTrade AI on any blockchain?",
      answer: "Currently, opTrade AI supports Ethereum. Our roadmap includes expanding to additional blockchains in Phase 6 of our development timeline."
    },
    {
      question: "How do I get started?",
      answer: "To begin, join our community on Telegram, acquire $OPTR tokens through Uniswap, and connect with our opTx Smart Trading Bot. As we progress through our roadmap, additional features and the full trading terminal will become available."
    }
  ];

  const toggleFaq = (index: number) => {
    if (expandedFaq === index) {
      setExpandedFaq(null);
    } else {
      setExpandedFaq(index);
    }
  };

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const currentSubtitle = headlines[currentHeadline].subtitle;
    
    const handleNextHeadline = () => {
      setTitleOpacity(0);
      timeoutId = setTimeout(() => {
        setCurrentHeadline((prev) => (prev + 1) % headlines.length);
        setTitleOpacity(1);
        setDisplayText('');
        setIsTyping(true);
      }, 500);
    };

    if (isTyping) {
      if (displayText.length < currentSubtitle.length) {
        timeoutId = setTimeout(() => {
          setDisplayText(currentSubtitle.slice(0, displayText.length + 1));
        }, 50);
      } else {
        timeoutId = setTimeout(() => {
          setIsTyping(false);
        }, 2000);
      }
    } else {
      if (displayText.length > 0) {
        timeoutId = setTimeout(() => {
          setDisplayText(displayText.slice(0, -1));
        }, 30);
      } else {
        timeoutId = setTimeout(handleNextHeadline, 500);
      }
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [displayText, isTyping, currentHeadline, headlines]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      // Throttle mouse position updates using requestAnimationFrame
      requestAnimationFrame(() => {
        setMousePosition({ x: e.clientX, y: e.clientY });
        
        if (cursorRef.current) {
          cursorRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
        }
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 800) {
        setShowStickyCTA(true);
      } else {
        setShowStickyCTA(false);
      }
      
      // Calculate scroll progress
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleGameMode = () => {
    if (isMobile) return; // Prevent activation on mobile
    
    setGameMode(!gameMode);
    // Optional: could add body class to prevent scrolling in game mode
    if (!gameMode) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  };

  return (
    <div className="min-h-screen bg-black text-green-500 font-mono relative">
      {/* Cinematic Mode Toggle Button - hidden on mobile */}
      {!isMobile && (
        <button
          onClick={toggleGameMode}
          className="fixed bottom-20 right-4 z-50 p-3 bg-black/80 rounded-full border border-green-500/30 
                    hover:bg-green-500/10 transition-all duration-300 group"
          title="Toggle Cinematic Mode"
        >
          {gameMode ? (
            <X className="w-5 h-5" />
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
              <path d="M4 6h16M4 12h16M4 18h16"></path>
              <path d="M6 9v6M18 9v6"></path>
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            </svg>
          )}
        </button>
      )}

      {/* Cinematic Mode Overlay - only shown if not mobile */}
      {gameMode && !isMobile && (
        <div 
          className="fixed inset-0 z-40 bg-black/80 backdrop-blur-md transition-all duration-500"
        >
          {/* Small exit button in corner */}
          <button
            onClick={toggleGameMode}
            className="absolute top-4 right-4 z-50 p-3 bg-black/60 rounded-full border border-green-500/30 
                      hover:bg-green-500/10 transition-all duration-300"
            title="Exit Cinematic Mode"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Cinematic Mode indicator */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 px-4 py-1.5 bg-black/40 
                        rounded-full border border-green-500/20 font-mono text-xs text-green-400/70
                        backdrop-blur-sm">
            CINEMATIC MODE • USE BUTTON TO EXIT
          </div>
          
          {/* Enhanced Particle Background in Cinematic Mode */}
          <div className="absolute inset-0 opacity-100 overflow-hidden">
            <div className="absolute inset-0 scale-125 transform-gpu">
              <ParticleBackground 
                enhanced={true}
              />
            </div>
          </div>
          
          {/* Additional visual effects */}
          <div className="absolute inset-0 opacity-40">
            <div className="absolute w-full h-full bg-[radial-gradient(ellipse_at_center,rgba(74,222,128,0.2),transparent_70%)]"></div>
            <div className="absolute w-full h-full animate-pulse">
              <div className="absolute top-1/4 left-1/2 w-96 h-96 -ml-48 -mt-48 bg-green-500/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-1/4 left-1/3 w-64 h-64 bg-green-500/20 rounded-full blur-2xl"></div>
              <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-green-500/15 rounded-full blur-2xl"></div>
            </div>
          </div>
        </div>
      )}

      {/* Cursor effects */}
      <div 
        ref={cursorRef}
        className="pointer-events-none fixed top-0 left-0 w-8 h-8 -ml-4 -mt-4 z-50
                   mix-blend-screen opacity-50"
        style={{
          background: 'radial-gradient(circle, rgba(74,222,128,1) 0%, rgba(74,222,128,0.2) 50%, transparent 70%)',
          transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
        }}
      />
      
      <div 
        className="pointer-events-none fixed top-0 left-0 w-16 h-16 -ml-8 -mt-8 z-40
                   mix-blend-screen opacity-30 transition-transform duration-500 ease-out"
        style={{
          background: 'radial-gradient(circle, rgba(74,222,128,0.3) 0%, rgba(74,222,128,0.1) 50%, transparent 70%)',
          transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
        }}
      />

      {/* Add padding-bottom to account for sticky CTA */}
      <div className="pb-24 sm:pb-28">
        {/* Rest of the content */}
        <EasterEgg />
        <BlockchainStatus />
        
        {/* Enhanced Professional Navigation */}
        <nav className="fixed w-full z-50 border-b border-green-500/20 bg-black/80 backdrop-blur-xl supports-[backdrop-filter]:bg-black/60">
          {/* Animated background gradient */}
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 via-green-500/3 to-teal-500/5 opacity-50"></div>
          
          {/* Subtle animated border */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500/50 to-transparent"></div>
          
          {/* Scroll progress indicator */}
          <div className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-emerald-500 to-green-500 transition-all duration-150 ease-out"
               style={{ width: `${scrollProgress}%` }}></div>
          
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="flex justify-between items-center h-16 lg:h-18">
              {/* Logo Section - Enhanced */}
              <div className="flex items-center gap-3 group">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/20 to-green-500/20 rounded-full blur-sm group-hover:blur-md transition-all duration-300"></div>
                  <img 
                    src="/assets/OPTR_LOGO.png" 
                    alt="opTrade AI Logo" 
                    className="relative h-8 w-8 lg:h-10 lg:w-10 transition-transform duration-300 group-hover:scale-110" 
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-lg lg:text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 to-green-400">
                    opTrade AI
                  </span>
                  <span className="text-xs text-green-500/60 font-mono hidden sm:block">
                    AI Trading Platform
                  </span>
                </div>
              </div>
              
              {/* Mobile menu button - Enhanced */}
              <div className="lg:hidden flex items-center">
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className="relative p-3 rounded-xl border border-green-500/30 hover:border-green-500/50 
                           bg-black/40 hover:bg-green-500/10 transition-all duration-300
                           backdrop-blur-sm group"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative">
                    {isMenuOpen ? (
                      <X className="h-5 w-5 text-green-400" />
                    ) : (
                      <Menu className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                </button>
              </div>

              {/* Desktop menu - Enhanced */}
              <div className="hidden lg:flex items-center space-x-1">
                <a href="#features" 
                   className="relative px-4 py-2 rounded-lg text-sm font-medium text-green-300/80 
                            hover:text-green-400 transition-all duration-300 group">
                  <span className="relative z-10">Features</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </a>
                
                <a href="#membership" 
                   className="relative px-4 py-2 rounded-lg text-sm font-medium text-green-300/80 
                            hover:text-green-400 transition-all duration-300 group">
                  <span className="relative z-10">Membership</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </a>
                
                <a href="#roadmap" 
                   className="relative px-4 py-2 rounded-lg text-sm font-medium text-green-300/80 
                            hover:text-green-400 transition-all duration-300 group">
                  <span className="relative z-10">Roadmap</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </a>
                
                <a href="https://spellbook.optrade.ai" 
                   target="_blank" 
                   rel="noopener noreferrer" 
                   className="relative px-4 py-2 rounded-lg text-sm font-medium text-green-300/80 
                            hover:text-green-400 transition-all duration-300 group flex items-center gap-2">
                  <span className="relative z-10">SpellBook</span>
                  <ExternalLink className="w-3 h-3 relative z-10" />
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-green-500/10 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </a>
                
                {/* Enhanced CTA Button */}
                <a href="https://t.me/optrade_ai" 
                   target="_blank" 
                   rel="noopener noreferrer" 
                   className="relative px-6 py-2.5 rounded-xl text-sm font-semibold text-black
                            bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400
                            transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-emerald-500/25
                            border border-emerald-500/20 group overflow-hidden">
                  <span className="relative z-10">Join Community</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-green-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </a>
              </div>
            </div>
          </div>

          {/* Enhanced Mobile menu */}
          <div className={`lg:hidden transition-all duration-300 ease-in-out ${isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
            <div className="px-4 pt-4 pb-6 space-y-2 border-t border-green-500/20 bg-black/95 backdrop-blur-xl">
              {/* Mobile menu items with enhanced styling */}
              <a href="#features" 
                 className="block px-4 py-3 rounded-xl text-sm font-medium text-green-300/80 
                          hover:text-green-400 hover:bg-gradient-to-r hover:from-emerald-500/10 hover:to-green-500/10
                          transition-all duration-300 group"
                 onClick={() => setIsMenuOpen(false)}>
                <span className="relative z-10">Features</span>
              </a>
              
              <a href="#membership" 
                 className="block px-4 py-3 rounded-xl text-sm font-medium text-green-300/80 
                          hover:text-green-400 hover:bg-gradient-to-r hover:from-emerald-500/10 hover:to-green-500/10
                          transition-all duration-300 group"
                 onClick={() => setIsMenuOpen(false)}>
                <span className="relative z-10">Membership</span>
              </a>
              
              <a href="#roadmap" 
                 className="block px-4 py-3 rounded-xl text-sm font-medium text-green-300/80 
                          hover:text-green-400 hover:bg-gradient-to-r hover:from-emerald-500/10 hover:to-green-500/10
                          transition-all duration-300 group"
                 onClick={() => setIsMenuOpen(false)}>
                <span className="relative z-10">Roadmap</span>
              </a>
              
              <a href="https://spellbook.optrade.ai" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="flex px-4 py-3 rounded-xl text-sm font-medium text-green-300/80 
                          hover:text-green-400 hover:bg-gradient-to-r hover:from-emerald-500/10 hover:to-green-500/10
                          transition-all duration-300 group items-center gap-2"
                 onClick={() => setIsMenuOpen(false)}>
                <span className="relative z-10">SpellBook</span>
                <ExternalLink className="w-3 h-3 relative z-10" />
              </a>
              
              {/* Enhanced mobile CTA */}
              <a href="https://t.me/optrade_ai" 
                 target="_blank" 
                 rel="noopener noreferrer"
                 className="block px-4 py-3 rounded-xl text-sm font-semibold text-black
                          bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400
                          transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-emerald-500/25
                          border border-emerald-500/20 mt-4 group overflow-hidden"
                 onClick={() => setIsMenuOpen(false)}>
                <span className="relative z-10">Join Community</span>
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-400/20 to-green-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </a>
            </div>
          </div>
        </nav>

        {/* Hero Section - Fixed for mobile devices */}
        <div className="relative min-h-screen flex flex-col pt-16 overflow-hidden">
          {/* Background elements */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {/* Lighter gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30 z-10" />
            
            {/* Modern grid pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-20" />
            
            {/* ParticleBackground - Increased opacity for more visibility */}
            <div className="absolute inset-0 z-0 opacity-70 overflow-hidden">
              <div className="absolute inset-0 pointer-events-none">
                <ParticleBackground />
              </div>
            </div>
          </div>
          
          <div className="flex-1 flex items-center justify-center p-4 relative z-20">
            <div className="text-center space-y-8 max-w-3xl mx-auto px-4">
              <div className="space-y-6">
                <div className="space-y-2">
                  <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold text-green-400 transition-all duration-1000 leading-tight"
                    style={{ opacity: titleOpacity }}>
                  {headlines[currentHeadline].title}
                </h1>
                  {/* Subtle gradient overlay on text */}
                  <div className="h-1 w-24 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full mx-auto opacity-60"></div>
                </div>
                <div className="relative">
                  <p className="text-lg sm:text-xl md:text-2xl text-green-300/80 min-h-[3rem] sm:min-h-[2.5rem] leading-relaxed">
                  {displayText}
                    <span className="animate-pulse text-green-400">|</span>
                </p>
                  {/* Subtle typing effect background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/5 to-transparent opacity-0 animate-pulse"></div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                {/* Trading Agent Button */}
                <a href="https://t.me/optx_smartbot" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="group relative max-w-[280px] mx-auto sm:mx-0 sm:w-auto px-6 py-4 rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                  {/* Glassmorphism background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 via-emerald-500/15 to-teal-500/20 backdrop-blur-sm border border-green-400/30 rounded-xl" />
                  {/* Animated border */}
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-400/50 via-emerald-400/30 to-teal-400/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
                  {/* Content */}
                  <div className="relative z-10 flex items-center justify-center gap-3 text-green-300 group-hover:text-green-200 transition-colors">
                    <div className="p-2 rounded-lg bg-green-500/20 group-hover:bg-green-500/30 transition-colors">
                      <Bot className="w-5 h-5 group-hover:animate-pulse" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-sm">Deploy Agent</div>
                      <div className="text-xs text-green-400/70 group-hover:text-green-300/70">AI Trading Bot</div>
                    </div>
                    <ExternalLink className="w-4 h-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </div>
                </a>
                
                {/* Terminal Access Button */}
                <a href="https://terminal.optrade.ai" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="group relative max-w-[280px] mx-auto sm:mx-0 sm:w-auto px-6 py-4 rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                  {/* Glassmorphism background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-cyan-500/15 to-teal-500/20 backdrop-blur-sm border border-blue-400/30 rounded-xl" />
                  {/* Animated border */}
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/50 via-cyan-400/30 to-teal-400/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
                  {/* Content */}
                  <div className="relative z-10 flex items-center justify-center gap-3 text-blue-300 group-hover:text-blue-200 transition-colors">
                    <div className="p-2 rounded-lg bg-blue-500/20 group-hover:bg-blue-500/30 transition-colors">
                      <Terminal className="w-5 h-5 group-hover:animate-pulse" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-sm">Open Terminal</div>
                      <div className="text-xs text-blue-400/70 group-hover:text-blue-300/70">Trading Interface</div>
                    </div>
                    <ExternalLink className="w-4 h-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </div>
                </a>

                {/* Get $OPTR Button */}
                <a href="https://app.uniswap.org/#/swap?inputCurrency=ETH&outputCurrency=0x72b658Bd674f9c2B4954682f517c17D14476e417" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="group relative max-w-[280px] mx-auto sm:mx-0 sm:w-auto px-6 py-4 rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-2xl">
                  {/* Glassmorphism background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-pink-500/15 to-rose-500/20 backdrop-blur-sm border border-purple-400/30 rounded-xl" />
                  {/* Animated border */}
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-400/50 via-pink-400/30 to-rose-400/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-sm" />
                  {/* Content */}
                  <div className="relative z-10 flex items-center justify-center gap-3 text-purple-300 group-hover:text-purple-200 transition-colors">
                    <div className="p-2 rounded-lg bg-purple-500/20 group-hover:bg-purple-500/30 transition-colors">
                      <TrendingUp className="w-5 h-5 group-hover:animate-pulse" />
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-sm">Get $OPTR</div>
                      <div className="text-xs text-purple-400/70 group-hover:text-purple-300/70">Buy Token</div>
                    </div>
                    <ExternalLink className="w-4 h-4 opacity-60 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </div>
                </a>
              </div>

              {/* Enhanced Token Address Section */}
              <div className="mt-8 flex flex-col items-center space-y-4">
                <div className="text-sm font-medium text-green-300/90 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  $OPTR Token Address
                </div>
                <div className="relative group max-w-[400px] w-full">
                  {/* Glassmorphism container */}
                  <div className="relative overflow-hidden rounded-xl border border-green-500/30 bg-gradient-to-r from-green-500/10 via-emerald-500/5 to-teal-500/10 backdrop-blur-sm">
                    {/* Animated background gradient */}
                    <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 via-emerald-400/10 to-teal-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Content */}
                    <div 
                      className="relative z-10 flex items-center gap-3 px-6 py-4 cursor-pointer transition-all duration-300 group-hover:scale-[1.02]"
                    onClick={copyToClipboard}
                  >
                      {/* Token icon */}
                      <div className="flex-shrink-0 p-2 rounded-lg bg-green-500/20 group-hover:bg-green-500/30 transition-colors">
                        <TrendingUp className="w-5 h-5 text-green-400" />
                      </div>
                      
                      {/* Address */}
                      <div className="flex-1 min-w-0">
                        <code className="text-sm font-mono text-green-300 group-hover:text-green-200 transition-colors block truncate">
                          {tokenAddress}
                        </code>
                        <div className="text-xs text-green-400/60 mt-1">
                          {copySuccess ? 'Copied!' : 'Click to copy'}
                        </div>
                      </div>
                      
                      {/* Copy button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard();
                      }}
                        className="flex-shrink-0 p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 transition-all duration-200 group-hover:scale-110"
                      title="Copy to clipboard"
                    >
                      {copySuccess ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                            className="w-4 h-4 text-green-400"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                            className="w-4 h-4 text-green-400 group-hover:text-green-300 transition-colors"
                        >
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                  
                  {/* Additional info */}
                  <div className="mt-3 text-center">
                    <div className="text-xs text-green-400/50">
                      Contract verified on Etherscan
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-30">
            <ChevronDown className="w-6 h-6" />
          </div>
        </div>

        {/* Spacer to ensure clean transition */}
        <div className="h-4 bg-black"></div>

        {/* Features Section */}
        <section id="features" className="py-20 bg-black/90 overflow-hidden relative z-30">
          {/* Enhanced background effects */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Animated gradient orbs */}
            <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-emerald-500/20 to-green-500/20 rounded-full blur-2xl animate-pulse"></div>
            <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full blur-2xl animate-pulse delay-1000"></div>
            <div className="absolute bottom-20 left-1/4 w-28 h-28 bg-gradient-to-r from-purple-500/20 to-violet-500/20 rounded-full blur-2xl animate-pulse delay-2000"></div>
            <div className="absolute bottom-40 right-1/3 w-20 h-20 bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
            
            {/* Subtle grid pattern */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(74,222,128,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(74,222,128,0.03)_1px,transparent_1px)] bg-[size:2rem_2rem] opacity-50"></div>
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-400 via-green-400 to-teal-400">
                  Your Trading Advantages
                </span>
              </h2>
              <p className="text-lg text-green-300/70 max-w-2xl mx-auto">
                Discover how each feature puts money in your pocket
              </p>
              
              {/* Decorative line */}
              <div className="mt-6 flex justify-center">
                <div className="h-1 w-24 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 rounded-full"></div>
              </div>
            </div>
            <div className="mb-16">
              <FeatureWidgets />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuresList.map((feature, index) => {
                const colorClasses = {
                  emerald: {
                    bg: 'bg-emerald-900/10 hover:bg-emerald-900/20',
                    border: 'border-emerald-500/30',
                    iconBg: 'bg-emerald-500/10 group-hover:bg-emerald-500/20',
                    iconColor: 'group-hover:text-emerald-400',
                    titleColor: 'group-hover:text-emerald-400',
                    shadow: 'hover:shadow-[0_0_15px_rgba(16,185,129,0.1)]',
                    expandedBg: 'bg-emerald-900/20'
                  },
                  blue: {
                    bg: 'bg-blue-900/10 hover:bg-blue-900/20',
                    border: 'border-blue-500/30',
                    iconBg: 'bg-blue-500/10 group-hover:bg-blue-500/20',
                    iconColor: 'group-hover:text-blue-400',
                    titleColor: 'group-hover:text-blue-400',
                    shadow: 'hover:shadow-[0_0_15px_rgba(59,130,246,0.1)]',
                    expandedBg: 'bg-blue-900/20'
                  },
                  purple: {
                    bg: 'bg-purple-900/10 hover:bg-purple-900/20',
                    border: 'border-purple-500/30',
                    iconBg: 'bg-purple-500/10 group-hover:bg-purple-500/20',
                    iconColor: 'group-hover:text-purple-400',
                    titleColor: 'group-hover:text-purple-400',
                    shadow: 'hover:shadow-[0_0_15px_rgba(147,51,234,0.1)]',
                    expandedBg: 'bg-purple-900/20'
                  },
                  orange: {
                    bg: 'bg-orange-900/10 hover:bg-orange-900/20',
                    border: 'border-orange-500/30',
                    iconBg: 'bg-orange-500/10 group-hover:bg-orange-500/20',
                    iconColor: 'group-hover:text-orange-400',
                    titleColor: 'group-hover:text-orange-400',
                    shadow: 'hover:shadow-[0_0_15px_rgba(249,115,22,0.1)]',
                    expandedBg: 'bg-orange-900/20'
                  },
                  teal: {
                    bg: 'bg-teal-900/10 hover:bg-teal-900/20',
                    border: 'border-teal-500/30',
                    iconBg: 'bg-teal-500/10 group-hover:bg-teal-500/20',
                    iconColor: 'group-hover:text-teal-400',
                    titleColor: 'group-hover:text-teal-400',
                    shadow: 'hover:shadow-[0_0_15px_rgba(20,184,166,0.1)]',
                    expandedBg: 'bg-teal-900/20'
                  },
                  pink: {
                    bg: 'bg-pink-900/10 hover:bg-pink-900/20',
                    border: 'border-pink-500/30',
                    iconBg: 'bg-pink-500/10 group-hover:bg-pink-500/20',
                    iconColor: 'group-hover:text-pink-400',
                    titleColor: 'group-hover:text-pink-400',
                    shadow: 'hover:shadow-[0_0_15px_rgba(236,72,153,0.1)]',
                    expandedBg: 'bg-pink-900/20'
                  },
                  red: {
                    bg: 'bg-red-900/10 hover:bg-red-900/20',
                    border: 'border-red-500/30',
                    iconBg: 'bg-red-500/10 group-hover:bg-red-500/20',
                    iconColor: 'group-hover:text-red-400',
                    titleColor: 'group-hover:text-red-400',
                    shadow: 'hover:shadow-[0_0_15px_rgba(239,68,68,0.1)]',
                    expandedBg: 'bg-red-900/20'
                  },
                  indigo: {
                    bg: 'bg-indigo-900/10 hover:bg-indigo-900/20',
                    border: 'border-indigo-500/30',
                    iconBg: 'bg-indigo-500/10 group-hover:bg-indigo-500/20',
                    iconColor: 'group-hover:text-indigo-400',
                    titleColor: 'group-hover:text-indigo-400',
                    shadow: 'hover:shadow-[0_0_15px_rgba(99,102,241,0.1)]',
                    expandedBg: 'bg-indigo-900/20'
                  },
                  yellow: {
                    bg: 'bg-yellow-900/10 hover:bg-yellow-900/20',
                    border: 'border-yellow-500/30',
                    iconBg: 'bg-yellow-500/10 group-hover:bg-yellow-500/20',
                    iconColor: 'group-hover:text-yellow-400',
                    titleColor: 'group-hover:text-yellow-400',
                    shadow: 'hover:shadow-[0_0_15px_rgba(234,179,8,0.1)]',
                    expandedBg: 'bg-yellow-900/20'
                  }
                };

                const colors = colorClasses[feature.color as keyof typeof colorClasses];

                return (
                  <div
                    key={feature.title}
                    onClick={() => setExpandedFeature(expandedFeature === feature.title ? null : feature.title)}
                    className={`group relative p-6 cursor-pointer
                      ${expandedFeature === feature.title 
                        ? `${colors.expandedBg} scale-105 z-10` 
                        : `${colors.bg}`} 
                      rounded-lg border ${colors.border}
                      transition-all duration-300 ease-out
                      transform hover:scale-[1.02]
                      ${expandedFeature && expandedFeature !== feature.title ? 'opacity-50' : 'opacity-100'}
                      animate-fadeIn
                      ${colors.shadow}
                    `}
                    style={{
                      '--delay': `${index * 100}ms`
                    } as React.CSSProperties}
                  >
                    {/* Add click indicator */}
                    <div className="absolute top-3 right-3 text-green-500/50 text-sm font-mono opacity-0 group-hover:opacity-100 transition-opacity">
                      [See your benefits]
                    </div>

                    {/* Gradient accent line */}
                    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${feature.gradient} rounded-t-lg opacity-60`}></div>

                    {/* Existing feature content */}
                    <div className="relative">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`p-2 rounded-lg ${colors.iconBg} transition-colors`}>
                          {React.cloneElement(feature.icon, {
                            className: `w-6 h-6 ${colors.iconColor} transition-colors`,
                            strokeWidth: 1.5
                          })}
                        </div>
                        <h3 className={`text-xl font-bold ${colors.titleColor} transition-colors`}>
                          {feature.title}
                        </h3>
                      </div>
                      
                      <p className="text-green-300/70 group-hover:text-green-300/90 transition-colors">
                        {feature.description}
                      </p>

                      {/* Expanded content */}
                      {expandedFeature === feature.title && featureDetails[feature.title] && (
                        <div className="mt-6 space-y-6 animate-fadeIn">
                          {/* Benefits */}
                          <div className="space-y-2">
                            <h4 className={`text-sm font-mono ${colors.titleColor.replace('group-hover:', '')}`}>HOW YOU PROFIT</h4>
                            <ul className="grid grid-cols-2 gap-2">
                              {featureDetails[feature.title].benefits.map((benefit: string) => (
                                <li key={benefit} className="flex items-center gap-2 text-sm">
                                  <span className={`${colors.iconColor.replace('group-hover:', '')}/50`}>⟩</span>
                                  <span className="text-green-300/70">{benefit}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Metrics */}
                          <div className="space-y-2">
                            <h4 className={`text-sm font-mono ${colors.titleColor.replace('group-hover:', '')}`}>YOUR EDGE</h4>
                            <div className="grid grid-cols-3 gap-2">
                              {Object.entries(featureDetails[feature.title].metrics).map(([key, value]) => (
                                <div key={key} className={`${colors.iconBg.replace('group-hover:', '')} p-2 rounded border ${colors.border}`}>
                                  <div className="text-xs text-green-300/50">{key}</div>
                                  <div className={`text-sm ${colors.iconColor.replace('group-hover:', '')} font-mono`}>{value}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="technology" className="py-20 bg-black/90 relative overflow-hidden">
          {/* Dynamic Background */}
          <div className="absolute inset-0 pointer-events-none">
            {/* Gradient background that follows mouse */}
            <div 
              className="absolute inset-0 bg-gradient-to-br from-green-900/20 via-black to-green-900/20"
              style={{
                transform: 'translate3d(0, 0, 0)',
                backgroundSize: '200% 200%',
                backgroundPosition: `${50 + (mousePosition.x / window.innerWidth) * 10}% ${50 + (mousePosition.y / window.innerHeight) * 10}%`,
                transition: 'background-position 0.3s ease-out'
              }}
            />
            
            {/* Animated grid lines */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(74,222,128,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(74,222,128,0.05)_1px,transparent_1px)] bg-[size:24px_24px]">
              <div 
                className="absolute inset-0 bg-[radial-gradient(circle_800px_at_50%_50%,rgba(0,0,0,0),rgba(0,0,0,0.9))]"
                style={{
                  // Make the radial gradient follow the mouse
                  background: `radial-gradient(circle 800px at ${mousePosition.x}px ${mousePosition.y}px, rgba(0,0,0,0), rgba(0,0,0,0.9))`
                }}
              />
            </div>

            {/* Glowing orb that follows mouse */}
            <div 
              className="absolute w-[500px] h-[500px] rounded-full blur-[100px] opacity-30"
              style={{
                background: 'radial-gradient(circle, rgba(74,222,128,0.15) 0%, transparent 70%)',
                transform: `translate(${mousePosition.x - 250}px, ${mousePosition.y - 250}px)`,
                transition: 'transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)'
              }}
            />

            {/* Scanning line effect */}
            <div className="absolute inset-0 overflow-hidden">
              <div 
                className="absolute h-[2px] w-full bg-gradient-to-r from-transparent via-green-500/20 to-transparent"
                style={{
                  animation: 'scan-line 3s ease-in-out infinite',
                  boxShadow: '0 0 20px rgba(74,222,128,0.2)'
                }}
              />
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <h2 className="text-3xl font-bold text-center mb-12">Our Technology</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Animation Container */}
              <div className="relative w-full overflow-hidden">
                <ProcessingAnimation />
              </div>

              {/* Description */}
              <div className="space-y-8">
                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-green-400">Data Collection & Analysis</h3>
                  <p className="text-green-300/70">
                    Our advanced AI systems continuously monitor blockchain transactions, market movements, 
                    and trading patterns across multiple networks to gather real-time intelligence.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-green-400">Advanced Neural Processing</h3>
                  <p className="text-green-300/70">
                    Our sophisticated algorithms and neural networks work in parallel to process market data, 
                    utilizing distributed computing for deep pattern recognition, multi-dimensional analysis, 
                    and high-precision trading opportunity identification.
                  </p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-2xl font-bold text-green-400">Strategic Execution</h3>
                  <p className="text-green-300/70">
                    Based on processed insights, our system generates trading signals and executes strategies 
                    through automated systems or provides actionable intelligence to traders.
                  </p>
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-4">
                  <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <div className="text-2xl font-bold text-green-400">24/7</div>
                    <div className="text-sm text-green-300/70">Continuous Monitoring</div>
                  </div>
                  <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <div className="text-2xl font-bold text-green-400">&lt;100ms</div>
                    <div className="text-sm text-green-300/70">Processing Time</div>
                  </div>
                  <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                    <div className="text-2xl font-bold text-green-400">99.9%</div>
                    <div className="text-sm text-green-300/70">System Uptime</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Membership Tiers */}
        <section id="membership" className="py-20 bg-black/90 relative overflow-hidden">
          {/* Dynamic background effects - unchanged */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-25" />
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <h2 className="text-4xl font-bold text-center mb-16">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-green-600">
                Access Tiers
              </span>
            </h2>


            {/* Tier cards with token requirements */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
              {membershipTiers.filter(tier => tier.name !== "Free").map((tier) => (
                <div key={tier.name} 
                    className={`relative group transition-all duration-300 ${tier.highlight ? 'hover:z-10' : ''}`}>
                  <div className="relative p-6 bg-black/60 backdrop-blur-sm rounded-lg border border-green-500/30 h-full flex flex-col items-center">
                    {/* Decorative corners */}
                    <div className="absolute top-0 left-0 w-4 h-4 border-t border-l border-green-500/50" />
                    <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-green-500/50" />
                    <div className="absolute bottom-0 left-0 w-4 h-4 border-b border-l border-green-500/50" />
                    <div className="absolute bottom-0 right-0 w-4 h-4 border-b border-r border-green-500/50" />

                    {/* Tier name */}
                    <div className="text-xl font-bold mb-6 flex items-center gap-2">
                      <span className="text-green-400">[</span>
                      {tier.name}
                      <span className="text-green-400">]</span>
                    </div>

                    <div className="mt-2 w-full">
                      <div className="relative h-2 bg-green-900/30 rounded-full overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-r from-green-500/50 to-green-400/50 w-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000" />
                      </div>
                      <div className="text-center mt-4">
                        <div className="text-2xl font-mono text-green-400/90 group-hover:text-green-400 transition-colors">
                          {tier.tokenRequirement}
                        </div>
                        <div className="text-green-300/50 text-sm mt-1">
                          $OPTR Required
                        </div>
                      </div>
                    </div>

                    {/* Premium badge */}
                    {tier.highlight && (
                      <div className="absolute -top-3 -right-3 bg-green-500/10 px-4 py-1 rounded-full text-xs font-mono border border-green-500/30 backdrop-blur-sm shadow-[0_0_15px_rgba(74,222,128,0.2)] group-hover:shadow-[0_0_25px_rgba(74,222,128,0.3)] transition-all duration-300">
                        PREMIUM
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Feature comparison table */}
            <div className="overflow-x-auto bg-black/60 backdrop-blur-sm rounded-lg border border-green-500/30 mt-8">
              <table className="w-full min-w-full divide-y divide-green-500/20">
                <thead>
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-green-400 uppercase tracking-wider">Feature</th>
                    {membershipTiers.map(tier => (
                      <th key={tier.name} className="px-6 py-4 text-center text-xs font-medium text-green-400 uppercase tracking-wider">
                        {tier.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-green-500/20">
                  {membershipFeatures.map((feature, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-green-500/5' : ''}>
                      <td className="px-6 py-4 text-sm text-green-300/80 whitespace-nowrap">
                        {feature.name}
                      </td>
                      
                      {membershipTiers.map(tier => (
                        <td key={`${tier.name}-${feature.name}`} className="px-6 py-4 text-center">
                          {feature.levels ? (
                            Object.prototype.hasOwnProperty.call(feature.levels, tier.name) ? (
                              <span className="text-green-400 font-medium">{feature.levels[tier.name]}</span>
                            ) : (
                              <span className="text-red-500">-</span>
                            )
                          ) : feature.tiers?.includes(tier.name) ? (
                            <svg className="w-5 h-5 mx-auto text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5 mx-auto text-red-500/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Beta notice - unchanged */}
            <div className="mt-12 text-center text-sm text-green-500/70">
              <span className="font-mono">[BETA]</span> Trading fees are currently waived during beta phase
            </div>
          </div>
        </section>

        {/* Enhanced "Revenue Model" Section */}
        <section id="revenue" className="relative py-20 overflow-hidden bg-gradient-to-br from-black via-zinc-900 to-black shadow-inner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-3xl font-bold text-green-400 tracking-wider">
                Revenue Model
              </h2>
              <button
                onClick={() => setIsStoryView(!isStoryView)}
                className="px-4 py-2 bg-green-500/10 hover:bg-green-500/20 rounded-lg border border-green-500/30
                           flex items-center gap-2 transition-all duration-300"
              >
                {isStoryView ? (
                  <>
                    <BarChart3 className="w-4 h-4" />
                    <span>View Metrics</span>
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-4 h-4" />
                    <span>View as Story</span>
                  </>
                )}
              </button>
            </div>

            {isStoryView ? (
              <RevenueStory />
            ) : (
              <>
                <p className="text-center text-green-300/70 mb-12 max-w-2xl mx-auto">
                  Our multi-stream revenue model combines sustainable tokenomics with scalable platform services
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
                  {/* Market Overview */}
                  <div className="bg-black/80 backdrop-blur-sm rounded-lg border border-green-500/30 p-6">
                    <h3 className="text-lg font-bold mb-4 text-green-400 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Market Opportunity
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center border-b border-green-500/20 pb-2">
                        <span className="text-green-300/70">Daily DEX Trading Volume</span>
                        <span className="text-green-400 font-mono">$1.2B+</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-green-500/20 pb-2">
                        <span className="text-green-300/70">Active DeFi Traders (Monthly)</span>
                        <span className="text-green-400 font-mono">850K+</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-green-500/20 pb-2">
                        <span className="text-green-300/70">Market Growth (YoY)</span>
                        <span className="text-green-400 font-mono">+85%</span>
                      </div>
                    </div>
                  </div>

                  {/* Target Metrics */}
                  <div className="bg-black/80 backdrop-blur-sm rounded-lg border border-green-500/30 p-6">
                    <h3 className="text-lg font-bold mb-4 text-green-400 flex items-center gap-2">
                      <LineChart className="w-5 h-5" />
                      Target Metrics (24 Months)
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center border-b border-green-500/20 pb-2">
                        <span className="text-green-300/70">Market Share Goal</span>
                        <span className="text-green-400 font-mono">2% Volume</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-green-500/20 pb-2">
                        <span className="text-green-300/70">Target User Base</span>
                        <span className="text-green-400 font-mono">50K+</span>
                      </div>
                      <div className="flex justify-between items-center border-b border-green-500/20 pb-2">
                        <span className="text-green-300/70">Projected Monthly Volume</span>
                        <span className="text-green-400 font-mono">$720M</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Revenue Streams */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                  {/* Trading Revenue */}
                  <div className="group relative hover:z-10 transition-all duration-500">
                    <div className="h-full p-6 bg-black/80 backdrop-blur-sm rounded-lg border border-green-500/30">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-green-500/10 rounded">
                          <BarChart3 className="w-6 h-6 text-green-400" />
                        </div>
                        <h3 className="text-lg font-bold text-green-400">Trading Revenue</h3>
                      </div>
                      <div className="space-y-3 mb-4">
                        <p className="text-green-300">Fixed trading fee structure:</p>
                        <ul className="space-y-2 text-sm text-green-300/70">
                          <li className="flex items-center gap-2">
                            <span className="text-green-500/50">⟩</span>
                            <span>0.001 ETH per trade</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="text-green-500/50">⟩</span>
                            <span>Simple, transparent pricing</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Membership Revenue */}
                  <div className="group relative hover:z-10 transition-all duration-500">
                    <div className="h-full p-6 bg-black/80 backdrop-blur-sm rounded-lg border border-green-500/30">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-green-500/10 rounded">
                          <Users className="w-6 h-6 text-green-400" />
                        </div>
                        <h3 className="text-lg font-bold text-green-400">Membership Revenue</h3>
                      </div>
                      <div className="space-y-3 mb-4">
                        <p className="text-green-300">Token-based membership:</p>
                        <ul className="space-y-2 text-sm text-green-300/70">
                          <li className="flex items-center gap-2">
                            <span className="text-green-500/50">⟩</span>
                            <span>XXX-XXXX $OPTR per tier</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="text-green-500/50">⟩</span>
                            <span>$xx-xxx monthly options</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Token Revenue */}
                  <div className="group relative hover:z-10 transition-all duration-500">
                    <div className="h-full p-6 bg-black/80 backdrop-blur-sm rounded-lg border border-green-500/30">
                      <div className="flex items-center gap-2 mb-4">
                        <div className="p-2 bg-green-500/10 rounded">
                          <PieChart className="w-6 h-6 text-green-400" />
                        </div>
                        <h3 className="text-lg font-bold text-green-400">Token Revenue</h3>
                      </div>
                      <div className="space-y-3 mb-4">
                        <p className="text-green-300">Sustainable tokenomics:</p>
                        <ul className="space-y-2 text-sm text-green-300/70">
                          <li className="flex items-center gap-2">
                            <span className="text-green-500/50">⟩</span>
                            <span>5% Trading Tax</span>
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="text-green-500/50">⟩</span>
                            <span>Token Utility Growth</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

              </>
            )}
          </div>

          {/* Background effects */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-green-500/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 right-0 w-1/4 h-1/4 bg-green-700/20 rounded-full blur-2xl animate-ping" />
          </div>
        </section>

        {/* Tokenomics */}
        <section id="tokenomics" className="bg-black/90 relative overflow-hidden">
          <TokenomicsSection />
        </section>

        {/* Roadmap - Optimized for mobile */}
        <section id="roadmap" className="py-16 md:py-32 bg-black relative overflow-hidden">
          <div className="max-w-[100rem] mx-auto px-4 sm:px-6 lg:px-8 relative">
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-center mb-8 md:mb-24">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-400 via-emerald-500 to-green-600">
                Development Timeline
              </span>
            </h2>

            <div className="relative">
              {/* Central connecting line - hidden on mobile, visible on larger screens */}
              <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-500/50 via-green-500/20 to-transparent 
                              shadow-[0_0_15px_rgba(74,222,128,0.2)] hidden md:block" />

              {/* Mobile vertical line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-green-500/50 via-green-500/20 to-transparent 
                             shadow-[0_0_15px_rgba(74,222,128,0.2)] md:hidden" />

              <div className="space-y-8 md:space-y-24 w-full">
                {roadmap.map((phase, index) => (
                  <div key={phase.phase} className="relative group">
                    {/* Phase indicator - centered on desktop, left-aligned on mobile */}
                    <div className="absolute md:left-1/2 left-4 top-0 md:-translate-x-1/2 flex flex-col items-center gap-2 md:gap-4
                      text-center z-10">
                      <div className={`w-6 h-6 md:w-10 md:h-10 relative ${phase.status === 'current' ? 'animate-pulse' : ''}`}>
                        <div className={`absolute inset-0 border-2 rotate-45 transform origin-center transition-all duration-300
                          ${phase.status === 'completed' ? 'border-green-400 bg-green-400/20 shadow-[0_0_15px_rgba(74,222,128,0.3)]' :
                            phase.status === 'current' ? 'border-green-500 bg-green-500/20 shadow-[0_0_20px_rgba(74,222,128,0.4)]' :
                            'border-green-800'}`} />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs md:text-sm font-mono">{index + 1}</span>
                        </div>
                      </div>
                      <div className="text-xs font-mono opacity-75 tracking-wider hidden md:block">{phase.timestamp}</div>
                    </div>

                    {/* Content card - alternating sides on desktop, always right on mobile */}
                    <div className={`w-full flex ${index % 2 === 0 ? 'md:justify-start' : 'md:justify-end'} justify-end`}>
                      <div className={`md:w-[calc(50%-3rem)] w-[calc(100%-2rem)] ml-2 md:ml-0 relative transition-all duration-300
                        ${phase.status === 'completed' ? 'opacity-75 hover:opacity-100' :
                          phase.status === 'current' ? 'opacity-100 md:scale-105' : 'opacity-50 hover:opacity-60'}`}>
                        
                        <div className={`relative bg-black/80 backdrop-blur-lg rounded-lg md:rounded-xl border transition-all duration-300
                          ${phase.status === 'completed' ? 'border-green-500/30 hover:border-green-500/50' :
                            phase.status === 'current' ? 'border-green-500 shadow-[0_0_30px_rgba(74,222,128,0.1)]' :
                            'border-green-800/30'}`}>
                          
                          {/* Header */}
                          <div className="p-3 md:p-6 border-b border-green-500/20">
                            <div className="flex items-start justify-between mb-2 md:mb-3">
                              <div className="flex flex-col gap-1 md:gap-4">
                                <span className="text-xs font-mono text-green-500/50 tracking-wider">
                                  PHASE_{index + 1}
                                </span>
                                <h3 className="text-lg md:text-2xl font-bold tracking-tight leading-tight">{phase.phase}</h3>
                                <div className="text-xs font-mono text-green-500/70 md:hidden">{phase.timestamp}</div>
                              </div>
                              <div className="text-xs font-mono bg-green-500/5 px-2 py-1 rounded-full border border-green-500/20 flex-shrink-0">
                                <span className={`
                                  ${phase.status === 'completed' ? 'text-green-400' :
                                    phase.status === 'current' ? 'text-green-500' : 'text-green-800'}
                                `}>
                                  {phase.completion}
                                </span>
                              </div>
                            </div>

                            {/* Progress bar */}
                            <div className="h-1 md:h-1.5 bg-green-900/30 rounded-full overflow-hidden">
                              <div className={`h-full transition-all duration-1000
                                ${phase.status === 'completed' ? 'bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.3)]' :
                                  phase.status === 'current' ? 'bg-green-500 shadow-[0_0_15px_rgba(74,222,128,0.4)]' : 'bg-green-800'}
                              `}
                              style={{ width: phase.completion }} />
                            </div>
                          </div>

                          {/* Items list */}
                          <ul className="p-3 md:p-6 space-y-3 md:space-y-4">
                            {phase.items.map((item) => (
                              <li key={item.text} className="flex items-start gap-2 md:gap-4 group/item">
                                <span className={`w-1.5 h-1.5 md:w-2.5 md:h-2.5 rounded-full transition-all duration-300 mt-1.5 md:mt-2 flex-shrink-0
                                  ${item.status === 'completed' ? 'bg-green-400 shadow-[0_0_10px_rgba(74,222,128,0.3)]' :
                                    item.status === 'in_progress' ? 'bg-green-500 animate-pulse shadow-[0_0_15px_rgba(74,222,128,0.4)]' :
                                    'bg-green-800'}`} />
                                
                                <div className="flex-1 flex flex-col md:flex-row md:items-center md:justify-between gap-1">
                                  <span className={`text-sm md:text-lg transition-colors duration-300 leading-tight
                                    ${item.status === 'completed' ? 'text-green-300/80' :
                                      item.status === 'in_progress' ? 'text-green-300' : 'text-green-300/50'}
                                  `}>
                                    {item.text}
                                  </span>
                                  <span className="text-xs font-mono text-green-500/50 tracking-wider">{item.date}</span>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Connecting line to center - desktop only */}
                        <div className={`absolute top-5 ${index % 2 === 0 ? 'right-0 translate-x-1/2' : 'left-0 -translate-x-1/2'}
                          w-12 h-0.5 bg-gradient-to-r from-green-500/50 to-transparent hidden md:block`} />
                        
                        {/* Mobile connecting line - always left */}
                        <div className="absolute top-3 left-0 -translate-x-[calc(100%+2px)] w-2 h-0.5 
                                        bg-gradient-to-l from-green-500/50 to-transparent md:hidden" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section - Add before Risk Warning */}
        <section id="faq" className="py-24 bg-black/90 relative overflow-hidden">
          {/* Background elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-25" />
            <div className="absolute top-0 right-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse" />
            <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
          </div>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <h2 className="text-4xl font-bold text-center mb-16">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-green-400 to-green-600">
                Frequently Asked Questions
              </span>
            </h2>

            <div className="space-y-6">
              {faqItems.map((item, index) => (
                <div 
                  key={index}
                  className="bg-black/60 backdrop-blur-sm rounded-lg border border-green-500/30 overflow-hidden transition-all duration-300"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-green-500/5 transition-colors"
                  >
                    <span className="text-lg font-medium text-green-400">{item.question}</span>
                    <span className="transform transition-transform duration-300">
                      {expandedFaq === index ? (
                        <ChevronDown className="w-5 h-5 rotate-180" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </span>
                  </button>
                  <div 
                    className={`overflow-hidden transition-all duration-300 ${
                      expandedFaq === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 py-4 border-t border-green-500/20 text-green-300/80 leading-relaxed">
                      {item.answer}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <p className="text-green-300/70">
                Still have questions?
              </p>
              <a 
                href="https://t.me/optrade_ai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block mt-4 px-6 py-3 bg-green-500/10 hover:bg-green-500/20 rounded-lg border border-green-500/30"
              >
                Ask in Our Community
              </a>
            </div>
          </div>
        </section>

        {/* Risk Warning */}
        <section className="py-8 bg-black/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center gap-2 text-yellow-500/80">
              <AlertTriangle className="w-5 h-5" />
              <p className="text-sm">Trading cryptocurrencies involves significant risk. Only trade with funds you can afford to lose.</p>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 bg-black/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-green-300/70">© 2025 opTrade AI. All rights reserved.</span>
              </div>
              <div className="flex items-center gap-4">
                <a href="https://doc.optrade.ai" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="text-sm text-green-300/70 hover:text-green-400 flex items-center gap-1">
                  <span>Docs</span>
                  <ExternalLink className="w-4 h-4" />
                </a>
                <a href="https://x.com/optrade_ai" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="hover:text-green-400">
                  <svg width="20" height="20" viewBox="0 0 1200 1227" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M714.163 519.284L1160.89 0H1055.03L667.137 450.887L357.328 0H0L468.492 681.821L0 1226.37H105.866L515.491 750.218L842.672 1226.37H1200L714.137 519.284H714.163ZM569.165 687.828L521.697 619.934L144.011 79.6944H306.615L611.412 515.685L658.88 583.579L1055.08 1150.3H892.476L569.165 687.854V687.828Z" />
                  </svg>
                </a>
                <a href="https://t.me/optrade_ai" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="hover:text-green-400">
                  <Send className="w-5 h-5" />
                </a>
                <a href="https://iq.wiki/wiki/optrade-ai" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="text-green-300/70 hover:text-green-400 transition-colors">
                  <img 
                    src="/assets/iqwiki-logo.svg" 
                    alt="IQ.wiki Logo" 
                    className="h-5 w-auto [&>path]:fill-current"
                  />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div> {/* Close the wrapper div */}

      {/* Sticky CTA */}
      <div className={`fixed bottom-0 left-0 right-0 p-4 bg-black/80 backdrop-blur-sm border-t border-green-500/30
                       transform transition-transform duration-300 z-50
                       ${showStickyCTA ? 'translate-y-0' : 'translate-y-full'}`}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-green-400 font-bold">Ready to optimize your trading?</div>
          <div className="flex gap-4">
            <a href="https://t.me/optrade_ai" 
               target="_blank" 
               rel="noopener noreferrer"
               className="px-4 py-2 bg-green-500/10 hover:bg-green-500/20 rounded-lg border border-green-500/30 whitespace-nowrap">
              Join Community
            </a>
            <a href="https://app.uniswap.org/#/swap?inputCurrency=ETH&outputCurrency=0x72b658Bd674f9c2B4954682f517c17D14476e417" 
               target="_blank" 
               rel="noopener noreferrer"
               className="px-4 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-black font-medium whitespace-nowrap">
              Get $OPTR
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;